import React from 'react';
import { Container, Card, Table, Button, Badge, Row, Col, Accordion } from 'react-bootstrap';

const PrivacyPolicy = () => {
  return (
    <Container className="py-3 py-md-5" style={{ maxWidth: '1300px' }}>
      <Card className="shadow-lg border-0 rounded-4 overflow-hidden">

        <Card.Header className="bg-gradient-primary text-white p-3 p-md-4" style={{
          background: 'linear-gradient(135deg, #b30000 0%, #dc3545 100%)'
        }}>
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
            <div>
              <h1 className="mb-1 fw-bold" style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)' }}>
                Data Protection & Privacy Policy
              </h1>
              <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
                <Badge bg="light" text="dark" className="px-2 py-1 px-md-3 py-md-2">
                  <span className="fw-normal small">Last updated:</span> <strong className="small">5 May 2026</strong>
                </Badge>
                <Badge bg="light" text="dark" className="px-2 py-1 px-md-3 py-md-2">
                  <span className="fw-normal small">ICO:</span> <strong className="small">00014146064</strong>
                </Badge>
              </div>
            </div>
            <div className="text-start text-md-end mt-2 mt-md-0">
              <div className="text-white-50 small">FireChecked</div>
              <div className="text-white-50 small">firechecked.co.uk</div>
            </div>
          </div>
        </Card.Header>

        <Card.Body className="p-3 p-md-5">

          <div className="bg-light rounded-3 p-3 mb-4 border-start border-4 border-danger">
            <Row className="g-2 g-md-3">
              <Col xs={12} md={6}>
                <div className="small text-muted">Contact</div>
                <div className="fw-medium text-break">alexh@firechecked.co.uk</div>
              </Col>
              <Col xs={12} md={6}>
                <div className="small text-muted">Registered address</div>
                <div className="fw-medium">
                  Foundation House<br />
                  42-48 London Road<br />
                  Reigate, Surrey, RH2 9QQ
                </div>
              </Col>
            </Row>
          </div>

          <section className="mb-4 mb-md-5">
            <h3 className="text-danger fw-semibold border-bottom pb-2">1. Purpose of this policy</h3>
            <p className="text-secondary">
              FireChecked provides fire door inspections, fire safety asset recording, reporting, client portal access,
              remedial tracking, and related compliance services.
            </p>
            <p className="text-secondary">
              This policy explains how FireChecked collects, uses, stores, protects, shares, and deletes personal data
              in line with the <strong>UK General Data Protection Regulation</strong>, the <strong>Data Protection Act 2018</strong>,
              and guidance from the <strong>Information Commissioner's Office</strong>.
            </p>
          </section>

          <section className="mb-4 mb-md-5">
            <h3 className="text-danger fw-semibold border-bottom pb-2">2. Who this policy applies to</h3>
            <div className="row g-2">
              {[
                'Clients and prospective clients',
                'Client employees, site contacts, building managers, responsible persons, accountable persons, contractors, and facilities teams',
                'FireChecked users of our inspection app, portal, or reporting systems',
                'Suppliers, subcontractors, and professional advisers',
                'People who contact us through our website, email, phone, forms, or social media',
                'Individuals who may appear incidentally in site photographs, videos, reports, or inspection evidence'
              ].map((item, idx) => (
                <div className="col-12 col-sm-6 col-lg-4" key={idx}>
                  <div className="d-flex align-items-start p-2 rounded-3 bg-light bg-opacity-50 h-100">
                    <span className="text-danger me-2 flex-shrink-0">•</span>
                    <span className="small">{item}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-4 mb-md-5">
            <h3 className="text-danger fw-semibold border-bottom pb-2">3. Our role under data protection law</h3>
            <div className="row g-3">
              <div className="col-md-6">
                <div className="p-3 h-100 bg-light rounded-3 border-start border-4 border-primary">
                  <h5 className="fw-bold">Data Controller</h5>
                  <p className="text-secondary small mb-0">
                    FireChecked acts as a data controller where we decide why and how personal data is used,
                    for example when handling enquiries, managing client accounts, issuing quotations,
                    sending service updates, or maintaining our business records.
                  </p>
                </div>
              </div>
              <div className="col-md-6">
                <div className="p-3 h-100 bg-light rounded-3 border-start border-4 border-success">
                  <h5 className="fw-bold">Data Processor</h5>
                  <p className="text-secondary small mb-0">
                    FireChecked acts as a data processor where we process personal data on behalf of a client,
                    for example when our portal, app, or inspection process stores site data, asset records,
                    photographs, videos, remedial notes, user activity, or reports on the client's behalf.
                  </p>
                </div>
              </div>
            </div>
            <p className="text-secondary small mt-3">
              Where FireChecked acts as a processor, we will process personal data only in accordance with
              the client's documented instructions, the relevant contract, and applicable data protection law.
            </p>
          </section>

          <section className="mb-4 mb-md-5">
            <h3 className="text-danger fw-semibold border-bottom pb-2">4. Personal data we collect</h3>
            <div className="table-responsive">
              <Table bordered hover className="align-middle" size="sm">
                <thead className="bg-light">
                  <tr>
                    <th className="text-uppercase small fw-bold" style={{ minWidth: '140px' }}>Data category</th>
                    <th className="text-uppercase small fw-bold">Examples</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td className="fw-medium">Contact details</td><td className="small text-secondary">Name, job title, company, email address, phone number, site address</td></tr>
                  <tr><td className="fw-medium">Account and portal data</td><td className="small text-secondary">Username, access permissions, organisation, assigned sites, login activity</td></tr>
                  <tr><td className="fw-medium">Client and contract data</td><td className="small text-secondary">Quotes, contracts, purchase orders, invoices, service history</td></tr>
                  <tr><td className="fw-medium">Inspection and asset data</td><td className="small text-secondary">Site contacts, building/floor/zone references, fire door asset records, remedial notes, responsible party names</td></tr>
                  <tr><td className="fw-medium">Photographic/video evidence</td><td className="small text-secondary">Images or videos of doors, frames, signage, corridors, access areas, or defects; individuals may appear incidentally</td></tr>
                  <tr><td className="fw-medium">Communication records</td><td className="small text-secondary">Emails, call notes, support tickets, meeting notes</td></tr>
                  <tr><td className="fw-medium">Technical data</td><td className="small text-secondary">IP address, browser type, device type, operating system, timestamps, audit logs</td></tr>
                  <tr><td className="fw-medium">Marketing preferences</td><td className="small text-secondary">Consent status, opt-out records, campaign engagement</td></tr>
                  <tr><td className="fw-medium">Supplier/subcontractor data</td><td className="small text-secondary">Contact details, qualifications, insurance documents, payment details</td></tr>
                </tbody>
              </Table>
            </div>
            <div className="alert alert-light border small">
              <span className="fw-bold">Note:</span> We do not intentionally collect special category data, such as health data,
              religious beliefs, or biometric data. If such information is captured accidentally, for example in a site photograph,
              we will minimise, restrict, blur, delete, or otherwise protect it where appropriate.
            </div>
          </section>

          <section className="mb-4 mb-md-5">
            <h3 className="text-danger fw-semibold border-bottom pb-2">5. How we collect personal data</h3>
            <div className="row g-2">
              {[
                'When you contact FireChecked by phone, email, website form, or social media',
                'When you request a quote, book an inspection, or become a client',
                'When you or your organisation uses the FireChecked app, dashboard, or client portal',
                'When our surveyors attend site and capture inspection evidence',
                'When you provide information during onboarding, support, or account management',
                'When we receive information from your employer, managing agent, landlord, facilities provider, contractor, or responsible person',
                'When we receive information from suppliers, subcontractors, or professional advisers'
              ].map((item, idx) => (
                <div className="col-12 col-sm-6" key={idx}>
                  <div className="d-flex align-items-start p-2 rounded-3">
                    <span className="text-danger me-2 flex-shrink-0">›</span>
                    <span className="small">{item}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-4 mb-md-5">
            <h3 className="text-danger fw-semibold border-bottom pb-2">6. Why we use personal data</h3>
            <div className="table-responsive">
              <Table bordered hover className="align-middle" size="sm">
                <thead className="bg-light">
                  <tr>
                    <th className="text-uppercase small fw-bold" style={{ minWidth: '120px' }}>Purpose</th>
                    <th className="text-uppercase small fw-bold">Example use</th>
                    <th className="text-uppercase small fw-bold" style={{ minWidth: '140px' }}>Likely lawful basis</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td className="fw-medium">Responding to enquiries</td><td className="small text-secondary">Replying to quote requests or service questions</td><td><Badge bg="light" text="dark" className="fw-normal small">Legitimate interests / pre-contract steps</Badge></td></tr>
                  <tr><td className="fw-medium">Providing inspection services</td><td className="small text-secondary">Booking surveys, attending site, recording findings, producing reports</td><td><Badge bg="light" text="dark" className="fw-normal small">Contract / legitimate interests</Badge></td></tr>
                  <tr><td className="fw-medium">Managing client portal access</td><td className="small text-secondary">Creating users, assigning permissions, maintaining audit logs</td><td><Badge bg="light" text="dark" className="fw-normal small">Contract / legitimate interests</Badge></td></tr>
                  <tr><td className="fw-medium">Compliance reporting</td><td className="small text-secondary">Maintaining inspection records, remedial action logs, evidence trails</td><td><Badge bg="light" text="dark" className="fw-normal small">Legal obligation / legitimate interests</Badge></td></tr>
                  <tr><td className="fw-medium">Client support</td><td className="small text-secondary">Troubleshooting, account assistance, report queries</td><td><Badge bg="light" text="dark" className="fw-normal small">Contract / legitimate interests</Badge></td></tr>
                  <tr><td className="fw-medium">Invoicing and finance</td><td className="small text-secondary">Billing, payment records, accounting</td><td><Badge bg="light" text="dark" className="fw-normal small">Contract / legal obligation</Badge></td></tr>
                  <tr><td className="fw-medium">Quality assurance</td><td className="small text-secondary">Reviewing surveyor outputs, checking report accuracy</td><td><Badge bg="light" text="dark" className="fw-normal small">Legitimate interests</Badge></td></tr>
                  <tr><td className="fw-medium">Safety and risk escalation</td><td className="small text-secondary">Alerting clients to serious fire safety defects</td><td><Badge bg="light" text="dark" className="fw-normal small">Legitimate interests / legal obligation</Badge></td></tr>
                  <tr><td className="fw-medium">Marketing to business contacts</td><td className="small text-secondary">Sending relevant service updates or newsletters</td><td><Badge bg="light" text="dark" className="fw-normal small">Consent or legitimate interests</Badge></td></tr>
                  <tr><td className="fw-medium">Website analytics and security</td><td className="small text-secondary">Monitoring performance, preventing abuse</td><td><Badge bg="light" text="dark" className="fw-normal small">Consent where required / legitimate interests</Badge></td></tr>
                </tbody>
              </Table>
            </div>
          </section>

          <section className="mb-4 mb-md-5">
            <h3 className="text-danger fw-semibold border-bottom pb-2">7. Legitimate interests</h3>
            <p className="text-secondary">Where we rely on legitimate interests, these may include:</p>
            <div className="row g-2">
              {[
                'Running and improving our fire safety inspection services',
                'Keeping accurate inspection, asset, and remedial records',
                'Protecting clients, building users, and FireChecked from safety, legal, or contractual risk',
                'Maintaining audit trails for compliance and quality assurance',
                'Communicating with business contacts about relevant services',
                'Securing our systems, app, portal, and data'
              ].map((item, idx) => (
                <div className="col-12 col-sm-6" key={idx}>
                  <div className="d-flex align-items-start p-2 rounded-3">
                    <span className="text-danger me-2 flex-shrink-0">›</span>
                    <span className="small">{item}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="alert alert-secondary border-0 mt-3 small">
              We will only rely on legitimate interests where we believe our interests are not overridden by
              the rights and freedoms of individuals.
            </div>
          </section>

          <section className="mb-4 mb-md-5">
            <h3 className="text-danger fw-semibold border-bottom pb-2">8. Site photographs, videos, and inspection evidence</h3>
            <p className="text-secondary">
              FireChecked surveyors may capture photographs and videos to evidence the condition of fire doors,
              frames, hardware, seals, gaps, signage, glazing, closers, access routes, or other relevant fire safety assets.
            </p>
            <p className="text-secondary">
              Our standard approach is to avoid capturing identifiable individuals wherever possible.
              Where people, names, screens, documents, access codes, or personal items appear incidentally,
              we may crop, blur, restrict, or delete the material where appropriate.
            </p>
            <p className="fw-medium mb-2">Inspection evidence is used for:</p>
            <div className="row g-2">
              {[
                'Fire door and asset condition reporting',
                'Compliance records',
                'Remedial action tracking',
                'Quality assurance',
                'Client handover and audit purposes',
                'Dispute resolution, where necessary'
              ].map((item, idx) => (
                <div className="col-12 col-sm-6 col-lg-4" key={idx}>
                  <div className="d-flex align-items-start p-2 rounded-3 bg-light bg-opacity-50 h-100">
                    <span className="text-danger me-2 flex-shrink-0">•</span>
                    <span className="small">{item}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-4 mb-md-5">
            <h3 className="text-danger fw-semibold border-bottom pb-2">9. NFC tags and asset records</h3>
            <p className="text-secondary">
              Where NFC tags, QR codes, or asset labels are used, FireChecked will not intentionally store
              sensitive personal data on the tag itself.
            </p>
            <p className="fw-medium mb-2">Tags should normally contain only:</p>
            <div className="row g-2">
              {[
                'A unique asset identifier',
                'A secure link or reference to the relevant asset record',
                'Non-sensitive technical metadata required to operate the system'
              ].map((item, idx) => (
                <div className="col-12 col-sm-6 col-lg-4" key={idx}>
                  <div className="d-flex align-items-start p-2 rounded-3 bg-light bg-opacity-50 h-100">
                    <span className="text-danger me-2 flex-shrink-0">•</span>
                    <span className="small">{item}</span>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-secondary small mt-3">
              Personal data, inspection notes, photographs, remedial actions, and user activity should be held
              securely within the FireChecked portal or approved system, not directly on the physical tag.
            </p>
          </section>

          <section className="mb-4 mb-md-5">
            <h3 className="text-danger fw-semibold border-bottom pb-2">10. Client portal and user accounts</h3>
            <p className="text-secondary">Where clients are given portal access, FireChecked may process:</p>
            <div className="row g-2">
              {[
                'User names',
                'Business email addresses',
                'Role permissions',
                'Login records',
                'Activity logs',
                'Reports viewed, downloaded, updated, or closed',
                'Remedial action updates'
              ].map((item, idx) => (
                <div className="col-12 col-sm-6 col-lg-4" key={idx}>
                  <div className="d-flex align-items-start p-2 rounded-3 bg-light bg-opacity-50 h-100">
                    <span className="text-danger me-2 flex-shrink-0">•</span>
                    <span className="small">{item}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="alert alert-light border mt-3 small">
              <strong>Client responsibility:</strong> Clients are responsible for ensuring that their own authorised
              users have appropriate access rights and that user access is removed when staff leave or no longer require access.
            </div>
            <p className="text-secondary small">
              FireChecked may suspend or restrict access where we reasonably believe there is a security, misuse,
              legal, or contractual concern.
            </p>
          </section>

          <section className="mb-4 mb-md-5">
            <h3 className="text-danger fw-semibold border-bottom pb-2">11. Sharing personal data</h3>
            <p className="text-secondary">We may share personal data with:</p>
            <div className="row g-2">
              {[
                'FireChecked employees, surveyors, reviewers, administrators, and account managers',
                'Clients, managing agents, landlords, responsible persons, accountable persons, or nominated representatives',
                'Approved subcontractors or specialist fire safety professionals',
                'Software, hosting, cloud storage, CRM, email, portal, accounting, and IT support providers',
                'Legal, insurance, finance, and professional advisers',
                'Regulators, public authorities, courts, or enforcement bodies where required by law',
                'Emergency or safety-related contacts where there is a serious risk requiring escalation'
              ].map((item, idx) => (
                <div className="col-12 col-sm-6" key={idx}>
                  <div className="d-flex align-items-start p-2 rounded-3 bg-light bg-opacity-50 h-100">
                    <span className="text-danger me-2 flex-shrink-0">•</span>
                    <span className="small">{item}</span>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-secondary small mt-3">
              We require third-party service providers to handle personal data securely and only for authorised purposes.
            </p>
          </section>

          <section className="mb-4 mb-md-5">
            <h3 className="text-danger fw-semibold border-bottom pb-2">12. Data security</h3>
            <p className="text-secondary">FireChecked applies appropriate technical and organisational measures to protect personal data. These may include:</p>
            <div className="row g-2">
              {[
                'Role-based user access',
                'Password protection and account controls',
                'Secure cloud hosting',
                'Audit logs',
                'Device security controls',
                'Staff training',
                'Restricted administrative access',
                'Secure backup procedures',
                'Supplier due diligence',
                'Data minimisation',
                'Incident response processes'
              ].map((item, idx) => (
                <div className="col-12 col-sm-6 col-lg-4" key={idx}>
                  <div className="d-flex align-items-start p-2 rounded-3 bg-light bg-opacity-50 h-100">
                    <span className="text-danger me-2 flex-shrink-0">•</span>
                    <span className="small">{item}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="alert alert-secondary border-0 mt-3 small">
              No system can be guaranteed to be completely secure, but we take reasonable steps to protect
              personal data from unauthorised access, loss, misuse, alteration, or disclosure.
            </div>
          </section>

          <section className="mb-4 mb-md-5">
            <h3 className="text-danger fw-semibold border-bottom pb-2">13. Data retention</h3>
            <p className="text-secondary">
              FireChecked will not keep personal data for longer than necessary. Retention periods should be
              documented in a retention schedule and should explain why data is retained and what happens at the
              end of the period, such as deletion, anonymisation, or archiving.
            </p>
            <div className="table-responsive">
              <Table bordered hover className="align-middle" size="sm">
                <thead className="bg-light">
                  <tr>
                    <th className="text-uppercase small fw-bold" style={{ minWidth: '160px' }}>Record type</th>
                    <th className="text-uppercase small fw-bold" style={{ minWidth: '130px' }}>Suggested retention period</th>
                    <th className="text-uppercase small fw-bold">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td className="fw-medium">Enquiry records where no contract follows</td><td>2 years</td><td className="small text-secondary">Sales follow-up and business records</td></tr>
                  <tr><td className="fw-medium">Client account records</td><td>Duration of contract + 6 years</td><td className="small text-secondary">Contract, tax, legal, and dispute purposes</td></tr>
                  <tr><td className="fw-medium">Fire door inspection reports</td><td>Contract duration + 6 years, or longer if agreed with clients</td><td className="small text-secondary">Compliance, audit, legal defence, fire safety record keeping</td></tr>
                  <tr><td className="fw-medium">Photographs/videos linked to inspection reports</td><td>Same as relevant inspection report</td><td className="small text-secondary">Evidence of findings and remedial actions</td></tr>
                  <tr><td className="fw-medium">Portal user records</td><td>Active account period + 2 years</td><td className="small text-secondary">Access control and audit trail</td></tr>
                  <tr><td className="fw-medium">Login and system audit logs</td><td>12–24 months unless needed longer</td><td className="small text-secondary">Security monitoring and investigation</td></tr>
                  <tr><td className="fw-medium">Financial records</td><td>6 years</td><td className="small text-secondary">Accounting and tax requirements</td></tr>
                  <tr><td className="fw-medium">Supplier/subcontractor records</td><td>Contract duration + 6 years</td><td className="small text-secondary">Contract and legal purposes</td></tr>
                  <tr><td className="fw-medium">Marketing records</td><td>Until opt-out, withdrawal of consent, or inactivity review</td><td className="small text-secondary">Marketing compliance</td></tr>
                  <tr><td className="fw-medium">Complaints and disputes</td><td>6 years after closure, or longer where legally required</td><td className="small text-secondary">Legal and insurance purposes</td></tr>
                </tbody>
              </Table>
            </div>
            <p className="text-secondary small">
              Where a client requires a different retention period, this should be set out in the contract,
              data processing agreement, or service schedule.
            </p>
          </section>

          <section className="mb-4 mb-md-5">
            <h3 className="text-danger fw-semibold border-bottom pb-2">14. Data accuracy</h3>
            <p className="text-secondary">
              We take reasonable steps to keep personal data accurate and up to date. Clients should notify
              FireChecked where site contacts, portal users, responsible persons, access permissions, or billing
              contacts change.
            </p>
          </section>

          <section className="mb-4 mb-md-5">
            <h3 className="text-danger fw-semibold border-bottom pb-2">15. Individual rights</h3>
            <p className="text-secondary">
              Individuals have rights under data protection law, which may include the right to be informed,
              access their personal data, correct inaccurate data, request deletion, restrict processing,
              object to certain processing, request data portability, withdraw consent where processing is
              based on consent, and complain to the <strong>Information Commissioner's Office</strong>.
            </p>
          </section>

          <section className="mb-4 mb-md-5">
            <h3 className="text-danger fw-semibold border-bottom pb-2">16. Marketing</h3>
            <p className="text-secondary">
              FireChecked may contact business contacts about relevant fire safety services, inspection renewals,
              software updates, compliance reminders, or related services. Individuals can opt out of marketing
              at any time by using the unsubscribe link in an email or contacting us.
              <strong> We will not sell personal data to third parties.</strong>
            </p>
          </section>

          <section className="mb-4 mb-md-5">
            <h3 className="text-danger fw-semibold border-bottom pb-2">17. Cookies and website tracking</h3>
            <p className="text-secondary">
              FireChecked may use cookies or similar technologies on its website to operate the website,
              improve performance, understand visitor activity, support security, and manage marketing or
              analytics where applicable. Where required, non-essential cookies will only be used with consent.
              A separate Cookie Policy may be provided on the website.
            </p>
          </section>

          <section className="mb-4 mb-md-5">
            <h3 className="text-danger fw-semibold border-bottom pb-2">18. Data breaches and incidents</h3>
            <p className="text-secondary">
              If FireChecked becomes aware of a personal data breach, we will investigate promptly and take
              appropriate action. Where required, we will notify affected clients, individuals, the ICO, or
              other relevant parties in line with applicable law and contractual obligations.
            </p>
          </section>

          <section className="mb-4 mb-md-5">
            <h3 className="text-danger fw-semibold border-bottom pb-2">19. Staff and subcontractor responsibilities</h3>
            <p className="text-secondary">
              Anyone working for or on behalf of FireChecked must process personal data only for authorised
              FireChecked purposes, keep client and site information confidential, avoid capturing unnecessary
              personal data, use approved systems and devices, report suspected data incidents promptly, and
              follow FireChecked procedures for access, storage, transfer, and deletion.
            </p>
          </section>

          <section className="mb-4 mb-md-5">
            <h3 className="text-danger fw-semibold border-bottom pb-2">20. Data protection by design</h3>
            <p className="text-secondary">
              FireChecked will aim to build privacy and data protection into its services by collecting only
              necessary data, using role-based access in the portal, avoiding sensitive information on NFC tags,
              maintaining audit trails, applying retention periods, reviewing supplier security, minimising
              personal data in photographs and reports, and using secure systems for client handover and
              remedial tracking.
            </p>
          </section>

          <section className="mb-4 mb-md-5">
            <h3 className="text-danger fw-semibold border-bottom pb-2">21. Changes to this policy</h3>
            <p className="text-secondary">
              FireChecked may update this policy from time to time to reflect changes in our services, systems,
              legal requirements, or ICO guidance. The latest version will be available on our website or
              provided on request.
            </p>
          </section>

          <section className="mb-4 mb-md-5">
            <h3 className="text-danger fw-semibold border-bottom pb-2">22. Contact</h3>
            <div className="bg-light p-3 p-md-4 rounded-3">
              <p className="text-secondary mb-3">
                Questions about this policy or FireChecked's handling of personal data should be sent to:
              </p>
              <div className="d-flex flex-wrap gap-3 gap-md-4">
                <div>
                  <div className="small text-muted">Email</div>
                  <strong className="text-danger text-break">alexh@firechecked.co.uk</strong>
                </div>
                <div>
                  <div className="small text-muted">Phone</div>
                  <strong>01483 338 356</strong>
                </div>
                <div>
                  <div className="small text-muted">Regulator</div>
                  <strong>Information Commissioner's Office</strong>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-4">
            <h4 className="text-secondary fw-semibold border-bottom pb-2">Reference sources for legal review</h4>
            <div className="row g-2">
              {[
                { label: 'ICO UK GDPR guidance', url: 'https://ico.org.uk/for-organisations/uk-gdpr-guidance-and-resources/' },
                { label: 'ICO lawful basis guidance', url: 'https://ico.org.uk/for-organisations/uk-gdpr-guidance-and-resources/lawful-basis/a-guide-to-lawful-basis/' },
                { label: 'ICO retention guidance', url: 'https://ico.org.uk/for-organisations/advice-and-services/audits/data-protection-audit-framework/toolkits/records-management/retention/' },
                { label: 'GOV.UK data protection overview', url: 'https://www.gov.uk/data-protection' }
              ].map((item, idx) => (
                <div className="col-12 col-sm-6" key={idx}>
                  <a href={item.url} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                    <div className="p-2 rounded-3 bg-light bg-opacity-50 border-start border-2 border-danger h-100">
                      <span className="small text-dark">{item.label}</span>
                      <span className="small text-muted d-block">↗</span>
                    </div>
                  </a>
                </div>
              ))}
            </div>
          </section>

          <footer className="text-muted small mt-4 mt-md-5 text-center pt-3 border-top">
            <p className="mb-0">Last updated: 5 May 2026 &bull; FireChecked &bull; ICO: 00014146064</p>
          </footer>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default PrivacyPolicy;